// Component: LiveAnalysisTab.jsx
// Purpose: Read-only live analysis for a deployed strategy — reuses the exact
//          Step 3 center pane (SmartChart + RDWatchDashboard with expand
//          toggle) via the shared StrategyAnalysisView component, plus (board
//          50-6, package P8b) a right-side panel (Depth / Order / Algo /
//          Indicators / Dashboard) mirroring SmartTrade.jsx's layout and tab-body pattern.
//          50-14: the Dashboard tab hosts a second RDWatchDashboard instance (portalled by
//          StrategyAnalysisView), and the desktop/wide panel collapses to a 40px icon rail
//          (state persisted per browser).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiBarChart2, FiShoppingCart, FiCpu, FiLayers, FiGrid, FiChevronRight } from 'react-icons/fi';

import StrategyAnalysisView from '../../Strategies/Modern/StrategyBuilder/components/StrategyAnalysisView';
import { historyFetchWindow } from '../../../components/SmartChart/historyFetchWindow';
import { liveInstrumentIdOf, useLevelHistory, useLevelHistoryPin, useLiveExecution, useTradingData, usePositionPin, usePerformancePin } from '../../../contexts/TradingDataContext';
import { useAuth } from '../../../contexts/AuthContext';
import { buildTradeMarker, resolveTradeExitAction, pricesToOutcome, snapToBar } from '../../../utils/tradeMarkerBuilder';
import { getOrderWorkingPrice } from '../../../utils/tradingTransforms';
import { buildCombinedLevelHistory, isPositionOpenForLiveBand, resolveOpenTradeSegment, openLevelsBySide, heldSideOf, compactTradeLevels } from '../../../utils/tradeLevelUtils';
import { Select } from '../../../components/ds/Select';
import { Segmented } from '../../../components/ds/Segmented';
import { Sheet } from '../../../components/ds/Sheet';
import { Button } from '../../../components/ds/Button';
import { Tooltip } from '../../../components/ds/Tooltip';
import { useBreakpoint } from '../../../hooks/useBreakpoint';
import StrategyService from '../../../api/services/strategies';
import OrdersService from '../../../api/services/orders';
import { useFetchDateRange } from '../../../hooks/trading/useFetchDateRange';
import { useLastTickTime } from '../../../hooks/trading/useLastTickTime';
import { useInstrumentTick } from '../../../hooks/trading/useInstrumentTick';
import { useOrderLevelsHistory } from '../../../hooks/trading/useOrderLevelsHistory';
import { useOpenTradeLevels } from '../../../hooks/trading/useOpenTradeLevels';
import { usePanelInstrument } from '../hooks/usePanelInstrument';
import { applyVisualOverrides } from '../../Strategies/Modern/StrategyBuilder/utils/chartDataDerivation';
import IndicatorsPanelTab from './IndicatorsPanelTab';
//* 50-8 — the ONE autosave store (also mounted by the Strategy Builder); this tab owns no PUT of its own.
import { useVisualOverridesStore } from '../../../hooks/useVisualOverrides';
import MarketDepth from '../../../components/SmartTrade/MarketDepth';
import SmartOrderForm from '../../../components/SmartTrade/SmartOrderForm';
import SmartTradeErrorBoundary from '../../../components/SmartTrade/SmartTradeErrorBoundary';
import SmartTradeSettings from '../../../components/SmartTrade/SmartTradeSettings';
import { services } from '../../../services/api';

//* 50-14 — ONE tab list: the Segmented options and the collapsed-rail buttons both read it (icon per entry).
const PANEL_TABS = [
  { value: 'depth', label: 'Depth', icon: FiBarChart2 },
  { value: 'order', label: 'Order', icon: FiShoppingCart },
  { value: 'algo', label: 'Algo', icon: FiCpu },
  { value: 'indicators', label: 'Indicators', icon: FiLayers },
  { value: 'dashboard', label: 'Dashboard', icon: FiGrid },
];

//--> 50-14 — per-browser collapsed flag. Guarded storage, same shape as StrategyToolbox.jsx AccordionGroup:
//    any throw (private mode / blocked storage) → default expanded.
const PANEL_COLLAPSED_KEY = 'deetrade.liveAnalysis.panelCollapsed';
const readPanelCollapsed = () => {
  try {
    return window.localStorage.getItem(PANEL_COLLAPSED_KEY) === 'true';
  } catch (e) {
    return false;
  }
};
const writePanelCollapsed = (value) => {
  try {
    window.localStorage.setItem(PANEL_COLLAPSED_KEY, value ? 'true' : 'false');
  } catch (e) {
    console.warn('[LiveAnalysisTab] could not persist panel collapsed flag', e);
  }
};

//--> 50-14 — the one grid-template table sizing every tier. `calc(25% - 6px)` reproduces the released
//    3/12 panel + 9/12 chart widths under the grid's `gap-2` (8px); both desktop tracks are
//    length-percentage values so the CSS transition interpolates (an fr-to-px pair would snap).
const PANEL_GRID_TEMPLATE = {
  compact: 'minmax(0, 1fr)',
  expanded: 'minmax(0, 1fr) calc(25% - 6px)',
  collapsed: 'minmax(0, 1fr) 40px',
};

//--> Panel collapse motion (ONE place). Guideline: opacity/size only, 150-300 ms, standard easing, exit shorter than enter,
//    prefers-reduced-motion = none (the `motion-reduce:!transition-none` classes beat these inline styles).
//    Collapse = content fades (FADE_OUT) THEN the column shrinks (COLUMN, delayed by FADE_OUT) THEN rail icons fade in.
//    Expand = column grows THEN content fades in (delayed by COLUMN). Pure CSS delays, no JS timers.
const PANEL_MOTION_MS = { fadeOut: 140, fadeIn: 180, column: 240 };
const PANEL_EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';
const panelColumnStyle = (collapsing) => ({
  transitionDuration: `${PANEL_MOTION_MS.column}ms`,
  transitionTimingFunction: PANEL_EASE,
  transitionDelay: `${collapsing ? PANEL_MOTION_MS.fadeOut : 0}ms`,
});
const panelContentStyle = (collapsed) => ({
  transitionProperty: 'opacity, visibility',
  transitionDuration: `${collapsed ? PANEL_MOTION_MS.fadeOut : PANEL_MOTION_MS.fadeIn}ms`,
  transitionTimingFunction: collapsed ? 'cubic-bezier(0.4, 0, 1, 1)' : 'cubic-bezier(0, 0, 0.2, 1)',
  transitionDelay: `${collapsed ? 0 : PANEL_MOTION_MS.column}ms`,
});
const PANEL_RAIL_FADE_STYLE = {
  animationDuration: `${PANEL_MOTION_MS.fadeIn}ms`,
  animationDelay: `${PANEL_MOTION_MS.fadeOut + PANEL_MOTION_MS.column}ms`,
  animationFillMode: 'backwards',
  animationTimingFunction: 'cubic-bezier(0, 0, 0.2, 1)',
};

/*>>> RIGHT-PANEL SUB-COMPONENTS (02-design.md §E/§G — P2 render boundary) <<<*/

//--> Depth body — same MarketDepth reuse as SmartTrade.jsx:168-180. `liveTick` already carries
//*   the 8 depth fields (useInstrumentTick) — no second WS subscription.
const DepthPanelBody = React.memo(function DepthPanelBody({ liveTick }) {
  return (
    <MarketDepth
      depth={{
        bid_prices: liveTick?.bid_prices,
        bid_qtys: liveTick?.bid_qtys,
        bid_orders: liveTick?.bid_orders,
        ask_prices: liveTick?.ask_prices,
        ask_qtys: liveTick?.ask_qtys,
        ask_orders: liveTick?.ask_orders,
      }}
      totalBuyQty={liveTick?.total_buy_qty}
      totalSellQty={liveTick?.total_sell_qty}
    />
  );
});

//--> Order body (§G, D36/M2-3) — `initialPrice` is the PARENT's already-frozen, STABLE value;
//*   for the same instrument it never changes across re-renders, so this memo never re-renders
//*   on a tick. SmartOrderForm itself is UNTOUCHED (R3.2 zero-diff).
const OrderPanelBody = React.memo(function OrderPanelBody({ instrument, initialPrice }) {
  const formInstrument = useMemo(() => ({ ...instrument, ltp: initialPrice }), [instrument, initialPrice]);
  return (
    <SmartTradeErrorBoundary>
      <SmartOrderForm instrument={formInstrument} />
    </SmartTradeErrorBoundary>
  );
});

//--> Algo body (§G, D42) — context-aware SmartTradeSettings, deployment context. Mounted
//*   UNCONDITIONALLY (R4.1 needs only deployment.deployment_id, never the instrument fetch).
const AlgoPanelBody = React.memo(function AlgoPanelBody({ deploymentId }) {
  return <SmartTradeSettings context={{ kind: 'deployment', deploymentId }} />;
});

const WaitingForLivePricePlaceholder = () => (
  <div className="flex h-full items-center justify-center p-4 text-center text-xs text-fg-3">
    Waiting for live price…
  </div>
);

const EXIT_ACTIONS = ['sl', 'tp', 'time_exit'];

//--> Resolve canonical action string from an OMS order record.
//*   Entry: derived from side.
//*   Exit: delegate to tradeMarkerBuilder.resolveTradeExitAction (the exit-reason registry).
//*        order.exit_reason is the CLOSE reason and wins when specific; order.leg_type is the
//*        born-as bracket role and is only a fallback when exit_reason is null or generic
//*        (rule_exit / signal). A converted exit (leg_type=stop_loss, exit_reason=
//*        pre_squareoff_flatten) therefore keeps its true close reason. Neither field is
//*        overwritten with the other.
//--> 50-5: terminal OMS order statuses (hoisted from the refetch effect; also gates the working-entry SIGNAL marker).
const TERMINAL = new Set(['FILLED', 'CANCELLED', 'REJECTED']);

const resolveOrderAction = (order) => {
  const legTypeRaw = (order.leg_type || order.action || '').toLowerCase();
  if (legTypeRaw === 'entry') {
    const side = String(order.side || '').toUpperCase();
    return side === 'SELL' || side === 'SHORT' ? 'enter_short' : 'enter_long';
  }
  //! The exit order's side is the CLOSING side (SELL closes a LONG, BUY closes a SHORT); the
  //!  resolver's side-default / flip expansion keys off the POSITION direction, so pass that.
  const closingSide = String(order.side || '').toUpperCase();
  return resolveTradeExitAction({
    exit_action: legTypeRaw,
    exit_reason: order.exit_reason,
    side: ['SELL', 'SHORT'].includes(closingSide) ? 'LONG' : 'SHORT',
  });
};

//--> Pick the signal/position_trigger node in the graph matching (action, stage).
//*   Falls back to null → buildTradeMarker uses TRADE_MARKER_DEFAULTS.
const findSignalNode = (nodes, action, stage) => {
  if (!Array.isArray(nodes)) return null;
  const stageNum = stage ?? 1;
  return nodes.find((n) => {
    const t = n?.type;
    if (t !== 'signal' && t !== 'position_trigger') return false;
    const d = n.data || {};
    const nodeStage = d.stage ?? 1;
    return d.action === action && nodeStage === stageNum;
  }) || null;
};

const LiveAnalysisTab = ({ deployment, strategyInstanceId }) => {
  const { user } = useAuth();
  const strategyInstanceIdRef = useRef(strategyInstanceId);
  strategyInstanceIdRef.current = strategyInstanceId;

  const [strategyConfig, setStrategyConfig] = useState({ nodes: [], edges: [] });
  const [configLoaded, setConfigLoaded] = useState(false);

  //--> Per-SII position/performance subscription pins (Spec 4) — mirror useLevelHistoryPin pattern
  usePositionPin(strategyInstanceId);
  usePerformancePin(strategyInstanceId);

  //--> Fetch the deployed version's graph so the chart + RDWatch render against
  //*   the running snapshot (not the latest edit in the builder). The strategy
  //*   version config also carries the resolved instrument object we pass down
  //*   — same source of truth StrategyBuilder uses (StrategyBuilder.jsx:860–866).
  useEffect(() => {
    let active = true;
    (async () => {
      if (!deployment?.strategy_id || !deployment?.version_id) return;
      try {
        const res = await StrategyService.getStrategyVersionById(deployment.strategy_id, deployment.version_id);
        const versionData = res?.data?.data || res?.data;
        if (active && versionData?.config) {
          setStrategyConfig(versionData.config);
          setConfigLoaded(true);
        }
      } catch (err) {
        console.error('[LiveAnalysisTab] strategy version fetch failed', err);
      }
    })();
    return () => { active = false; };
  }, [deployment?.strategy_id, deployment?.version_id]);

  //--> Seed prior closed trades from REST on mount; merge with live WS stream.
  const { trades: priorTrades, refetch: refetchPriorTrades } = useOrderLevelsHistory(strategyInstanceId);
  //! Eager-pin the trade_context WS subscription at tab-mount time so the
  //!  entry-bar snapshot lands in the buffer. Strategy-worker publishes that
  //!  snapshot at bar N close (before OMS fill completes); the
  //!  positions-driven subscribe path in TradingDataContext only fires AFTER
  //!  the fill event reaches the frontend, so without the pin the WS bridge
  //!  has no client to deliver to and the snapshot is dropped. See
  //!  websocket-v2/app/handlers/subscription.py broadcast_to_subscription —
  //!  no buffering for late subscribers. The pin is ref-counted: safe even
  //!  if this tab mounts alongside others that need the same sii.
  useLevelHistoryPin(strategyInstanceId);
  const liveSnapshots = useLevelHistory(strategyInstanceId);
  //! `positions` declared further down — order matters because levelHistory below
  //!  consumes isPositionOpen derived from it. Keep `positions` ABOVE this if you
  //!  refactor the layout. Falls back to "open" so the band still projects on
  //!  initial mount before the positions WS state has hydrated.
  const { orders: liveOrders, positions } = useTradingData({ strategyInstanceId });
  //! Disambiguate two cases that both surface as `positions == []`:
  //!   - hydration race (no snapshots yet)              → default OPEN
  //!   - post-close eviction after qty=0                → CLOSED
  //! Prior fix relied on the fallback "empty positions → OPEN" always; that
  //! made buildLiveLevelHistory's final segment keep exit_time=null after a
  //! close, and TradeBandSeries' forward-projection then extended the band
  //! 2 bars beyond the exit candle (Issue D — regression reported 2026-04-22).
  //! Buffer presence distinguishes the two cases — see isPositionOpenForLiveBand.

  //--> Resolved instrument id (moved up from the tick-time block below — SINGLE source of truth,
  //*   consumed by the open-trade band hook here AND useLastTickTime/useInstrumentTick below).
  //*   Prefer resolved_instrument_id (post-rollover) then fall back to raw instrument_id.
  const resolvedInstrumentId = liveInstrumentIdOf(deployment);

  //--> issue-sltp-band-persistence: durable open-trade SL/TP band hydration (survives WS
  //*   trade_context silence — mount/reload/reconnect). Sibling to useOrderLevelsHistory; exposes
  //*   the per-account open_trades ARRAY. account_id/execution_mode are server-derived (not sent).
  //*   Reads deployment?.mode INLINE (not the chart-window `const mode` below with its 'rd' fallback).
  const { openTrades, refetch: refetchOpenTrade } = useOpenTradeLevels(
    strategyInstanceId, { instrumentId: resolvedInstrumentId, mode: deployment?.mode },
  );
  //! rev4 account filter: null = ALL. Dropdown (rendered only when openTrades.length > 1) sets it.
  const [selectedAccountId, setSelectedAccountId] = useState(null);

  const isPositionOpen = useMemo(
    () => isPositionOpenForLiveBand(positions, liveSnapshots),
    [positions, liveSnapshots],
  );
  //--> Apply the account filter (null = ALL); a vanished selection falls back to ALL so the chart
  //*   never blanks on a stale account id (its position closed out of openTrades).
  const shownTrades = useMemo(() => {
    if (!selectedAccountId) return openTrades;
    const one = openTrades.filter((g) => g.account_id === selectedAccountId);
    return one.length ? one : openTrades;
  }, [openTrades, selectedAccountId]);
  //--> One durable open-trade segment PER shown account group; the WS tail attaches to at most the
  //*   one identity-matching group (others are REST-only). buildCombinedLevelHistory (R7) unchanged.
  const openSegs = useMemo(
    () => shownTrades.map((g) => resolveOpenTradeSegment(g, liveSnapshots, { isPositionOpen })).filter(Boolean),
    [shownTrades, liveSnapshots, isPositionOpen],
  );
  const priorPlus = useMemo(
    () => (openSegs.length ? [...priorTrades, ...openSegs] : priorTrades),
    [priorTrades, openSegs],
  );
  const levelHistory = useMemo(
      () => buildCombinedLevelHistory(priorPlus, liveSnapshots, { isPositionOpen }),
      [priorPlus, liveSnapshots, isPositionOpen],
  );
  //--> 50-17: the dashboard SL/TP rows read the SAME open-trade levels the chart's price lines draw.
  //*   Value-keyed so a new levelHistory identity with the same levels never re-renders the memoised dashboard.
  //!   `held` = the side the OMS position record says is open (heldSideOf) — flat / in-position is never
  //!   inferred from missing levels.
  //*   `trades` = the chart's trade segments (compact) for the hovered-bar lookup (levelsAtBar).
  const positionLevelsKey = JSON.stringify({ ...openLevelsBySide(levelHistory), held: heldSideOf(positions), trades: compactTradeLevels(levelHistory) });
  const positionLevels = useMemo(() => JSON.parse(positionLevelsKey), [positionLevelsKey]);

  //--> issue-sltp-band-persistence (D2/AC-4): event-driven band CLEAR. Refetch the open-trade lane
  //*   ONLY on a real edge — never on every liveOrders bump (~776/day = a refetch storm on the
  //*   placement-critical core). Three guards: prevRef open→closed edge, a per-leg terminal-transition
  //*   dedup Set, and a ~250ms debounce.
  const prevPositionOpenRef = useRef(isPositionOpen);
  const seenTerminalLegIdsRef = useRef(new Set());
  const refetchDebounceRef = useRef(null);
  const scheduleOpenTradeRefetch = useCallback(() => {
    if (refetchDebounceRef.current) clearTimeout(refetchDebounceRef.current);
    refetchDebounceRef.current = setTimeout(() => { refetchOpenTrade(); }, 250);
  }, [refetchOpenTrade]);

  useEffect(() => {
    const prevOpen = prevPositionOpenRef.current;
    prevPositionOpenRef.current = isPositionOpen;
    let shouldRefetch = false;
    //--> (a) position open→closed edge (prevRef) — fires exactly once on the transition.
    if (prevOpen === true && isPositionOpen === false) shouldRefetch = true;
    //--> (b) a tracked SL/TP leg FIRST reaching terminal — per-leg dedup Set → once per leg, never
    //*   on every subsequent bump. liveOrders carry leg_type (from WS `action`) + status.
    const TRACKED_LEGS = new Set(['stop_loss', 'take_profit']);
    for (const o of (liveOrders || [])) {
      const legType = String(o.leg_type || o.action || '').toLowerCase();
      const st = String(o.status || '').toUpperCase();
      //--> 50-5: an ENTRY order reaching FILLED is one more edge (its legs + fill_time now exist on the
      //*   REST read). Same dedup Set as the legs -- once per order id, never a second tracker.
      const isFilledEntry = legType === 'entry' && st === 'FILLED';
      if (!isFilledEntry) {
        if (!TRACKED_LEGS.has(legType)) continue;
        if (!TERMINAL.has(st)) continue;
      }
      const id = o.id || o.order_id;
      if (id == null || seenTerminalLegIdsRef.current.has(id)) continue;
      seenTerminalLegIdsRef.current.add(id);
      shouldRefetch = true;
    }
    if (shouldRefetch) scheduleOpenTradeRefetch();
  }, [isPositionOpen, liveOrders, scheduleOpenTradeRefetch]);

  //--> Display-record store (board 50-6/50-8, §C/§H) — one instance per strategy; feeds the
  //*   Indicators panel body AND the chokepoint merge below. GET failure falls back to `{}`
  //*   inside the hook itself (D38) — the chart is never blanked by an overrides-fetch failure.
  const overridesStore = useVisualOverridesStore(deployment?.strategy_id, strategyConfig.nodes);

  //--> D56 visual-gate fix-up — indicator registry, loaded ONCE (same call StrategyBuilder.jsx:698
  //*   makes), reduced to a {indicator name -> capitalised category} map so the Indicators tab's
  //*   accordion subgroups mirror the Toolbox's own category grouping (one
  //*   authority: the registry, never a guessed/hardcoded category list). Failure -> empty map,
  //*   no error UI (every node then falls to the "Custom" bucket, same as an uncategorised
  //*   registry entry would in the Toolbox).
  const [categoryByIndicator, setCategoryByIndicator] = useState({});
  const [indicatorMetadata, setIndicatorMetadata] = useState([]);
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await services.indicators.list();
        if (active && res.data?.status === 'success') {
          const map = {};
          (res.data.indicators || []).forEach((ind) => {
            const cat = ind.category || 'Others';
            map[ind.name] = cat.charAt(0).toUpperCase() + cat.slice(1);
          });
          setCategoryByIndicator(map);
          setIndicatorMetadata(res.data.indicators || []);
        }
      } catch (err) {
        console.error('[LiveAnalysisTab] indicator registry fetch failed', err);
      }
    })();
    return () => { active = false; };
  }, []);

  //--> D35 — ONE merge chokepoint feeding ALL FOUR display consumers (chart `nodes`,
  //*   levelDisplayNodes, tradeMarkers, RDWatchDashboard fan-out via StrategyAnalysisView).
  //*   `computationNodes` (passed separately below) stays the RAW `strategyConfig.nodes` so a
  //*   visibility/style-only override never changes useIndicators' functional key.
  const effectiveNodes = useMemo(
    () => applyVisualOverrides(strategyConfig?.nodes || [], overridesStore.overridesSaved, overridesStore.draftOverrides),
    [strategyConfig?.nodes, overridesStore.overridesSaved, overridesStore.draftOverrides],
  );

  const levelDisplayNodes = useMemo(() => {
    return effectiveNodes.filter((n) => EXIT_ACTIONS.includes(n?.data?.action));
  }, [effectiveNodes]);


  //--> Resolve the full instrument object from the version config. SmartChart
  //*   needs {exchange, symbol, instrument_type, instrument_id} — a raw string
  //*   ID alone leaves the chart unable to resolve OHLC feed.
  const instrument = useMemo(() => {
    if (Array.isArray(strategyConfig?.instruments) && strategyConfig.instruments.length > 0) {
      return strategyConfig.instruments[0];
    }
    if (strategyConfig?.instrument) return strategyConfig.instrument;
    return null;
  }, [strategyConfig]);

  //--> Rollover days for F&O contract resolution — pulled from general config.
  //! F5 — rollover_days is a live MDMS stitch instruction (harmless only while MDMS
  //! zeroes it behind is_continuous); gate here so a PINNED strategy never sends a
  //! nonzero value, mirroring StrategyBuilder.jsx's isContinuous gate.
  const rolloverDays = strategyConfig?.general?.isContinuous
    ? (Number(strategyConfig?.general?.rolloverDays) || 0)
    : 0;
  //! LB-8 (F-06) — structural companion to rolloverDays: thread the raw flag itself
  //! (not just the already-gated rolloverDays value) down to SmartChart so
  //! resolveRolloverDays() can gate structurally, independent of instrument.rollover_days.
  const isContinuous = Boolean(strategyConfig?.general?.isContinuous);

  const [historicalOrders, setHistoricalOrders] = useState([]);
  const [gapOrders, setGapOrders] = useState([]);

  useEffect(() => {
    setGapOrders([]);
  }, [strategyInstanceId]);

  //--> One paginated loader for both the chart window and committed gap repairs.
  const fetchOrdersWindow = useCallback(async (fromIso, toIso) => {
    if (!strategyInstanceId || !fromIso || !toIso) return [];
    let allOrders = [];
    let skip = 0;
    const limit = 200;
    while (allOrders.length < 1000) {
      const res = await OrdersService.getOrders({
        strategyInstanceId,
        fromBarStart: fromIso,
        toBarStart: toIso,
        limit,
        skip,
      });
      const fetched = res?.data?.data?.orders || [];
      allOrders = allOrders.concat(fetched);
      if (fetched.length < limit) break;
      skip += limit;
    }
    return allOrders;
  }, [strategyInstanceId]);

  //--> liveOrders is destructured at line 116 alongside `positions` from the same
  //*   useTradingData({ strategyInstanceId }) call; reused below.

  //--> Combined Orders: Real-time WS + Historical REST. WS wins on overlap.
  const orders = useMemo(() => {
    const map = new Map();
    // Insert history first
    historicalOrders.forEach(o => map.set(o.id || o.order_id, o));
    gapOrders.forEach(o => map.set(o.id || o.order_id, o));
    // Overwrite with live stream updates
    (liveOrders || []).forEach(o => map.set(o.id || o.order_id, o));
    return Array.from(map.values());
  }, [historicalOrders, gapOrders, liveOrders]);

  const tradeMarkers = useMemo(() => {
    if (!Array.isArray(orders) || orders.length === 0) return [];
    const tf = deployment?.timeframe || '1m';
    //--> 50-5 R2: entry_order_id -> actual fill_time from the levels REST (the one FE source, shared with the band).
    const fillByEntry = new Map();
    for (const t of [...(priorTrades || []), ...(openTrades || [])]) {
      if (t?.entry_order_id != null && t?.fill_time) fillByEntry.set(String(t.entry_order_id), t.fill_time);
    }
    //--> D35/R7.1 — marker styling resolution reads the SAME effective (version ⊕ saved ⊕
    //*   draft) node set as the chart/levelDisplayNodes, never the raw strategyConfig.nodes.
    const nodes = effectiveNodes;
    //--> Exit marker colour = realised outcome, paired from THIS orders list (no fetch, no levels
    //*   read): the exit's entry is the row whose id == exit.parent_order_id, when NO parent id is set, the single FILLED
    //*   entry sharing exit.lifecycle_id. Zero / many candidates, non-FILLED entry => neutral.
    const entryById = new Map();
    const entriesByLifecycle = new Map();
    for (const o of orders) {
      if (String(o?.leg_type || o?.action || '').toLowerCase() !== 'entry') continue;
      if (String(o.status || '').toUpperCase() !== 'FILLED') continue;
      if (!Number.isFinite(Number(o.filled_price)) || o.filled_price == null) continue;
      const oid = o.id ?? o.order_id;
      if (oid != null) entryById.set(String(oid), o);
      if (o.lifecycle_id != null) {
        const bucket = entriesByLifecycle.get(String(o.lifecycle_id)) || [];
        bucket.push(o);
        entriesByLifecycle.set(String(o.lifecycle_id), bucket);
      }
    }
    const pairedEntry = (exit) => {
      //! A parent id that is SET but absent from the window is neutral -- never guess via the
      //!  lifecycle (a multi-stage lifecycle could pair the wrong stage).
      if (exit.parent_order_id != null && exit.parent_order_id !== '') {
        return entryById.get(String(exit.parent_order_id)) || null;
      }
      const bucket = exit.lifecycle_id != null ? entriesByLifecycle.get(String(exit.lifecycle_id)) : null;
      return bucket && bucket.length === 1 ? bucket[0] : null;
    };
    const out = [];
    for (const order of orders) {
      const orderStatus = String(order.status || '').toUpperCase();
      const isEntryOrder = String(order.leg_type || order.action || '').toLowerCase() === 'entry';
      //--> 50-5 R2: a WORKING entry draws one SIGNAL marker at its requested bar + price; CANCELLED /
      //*   REJECTED (no fill) draws nothing. Non-entry orders keep the FILLED-only rule.
      if (isEntryOrder && !TERMINAL.has(orderStatus)) {
        const workingPrice = getOrderWorkingPrice(order);
        const reqTime = order.bar_start ? snapToBar(order.bar_start, tf, order.bar_start) : null;
        if (workingPrice != null && reqTime != null) {
          const m = buildTradeMarker(
            { time: reqTime, price: workingPrice, action: 'signal', side: String(order.side || ''), stage: order.stage ?? 1 },
            null,
          );
          if (m) out.push(m);
        }
        continue;
      }
      if (orderStatus !== 'FILLED') continue;
      if (order.filled_price == null) continue;

      const action = resolveOrderAction(order);
      if (!action) continue;

      //--> Snap to active bar boundary via shared snapToBar so live OMS markers
      //*   align with the candle series (Chunk 3b). Preserves richer styling
      //*   resolution via findSignalNode + buildTradeMarker below — replacing
      //*   with buildMarkersFromOrders would drop node-based visual_settings
      //*   (shape/color/position/label overrides per signal node).
      //
      //! Time-domain: chart axis is bar-clock (OHLC bar.time). Use order.bar_start
      //!  (the bar-clock timestamp of the originating signal) — NOT updated_at /
      //!  created_at which are wall-clock and, in simulation / historic replay,
      //!  diverge from bar-clock by months. Wall-clock timestamps place markers
      //!  off-screen to the right of every replayed bar. Fall back to wall-clock
      //!  only for rows that pre-date the 2026-04-19 bar_start migration.
      const rawTime = order.bar_start || order.timestamp || order.updated_at || order.created_at;
      let time = snapToBar(rawTime, tf);
      if (time == null) continue;

      //--> 50-5 R2: FILLED entry with a known fill_time on a DIFFERENT bar than the requested one -> the
      //*   entry marker moves to the fill bar (at filled_price) and a SIGNAL marker stays on the requested bar.
      if (isEntryOrder && order.bar_start) {
        const fillTime = fillByEntry.get(String(order.id ?? order.order_id));
        if (fillTime) {
          const fillBar = snapToBar(fillTime, tf, order.bar_start);
          const sigBar = snapToBar(order.bar_start, tf, order.bar_start);
          if (fillBar != null && sigBar != null && fillBar !== sigBar) {
            time = fillBar;
            const sigPrice = getOrderWorkingPrice(order) ?? Number(order.filled_price);
            const sigMarker = buildTradeMarker(
              { time: sigBar, price: sigPrice, action: 'signal', side: String(order.side || ''), stage: order.stage ?? 1 },
              null,
            );
            if (sigMarker) out.push(sigMarker);
          }
        }
      }

      //! Out-of-range markers (e.g. orders from a PRIOR sim run that covered a
      //! different historic window) are rejected DOWNSTREAM by SmartChart's
      //! own filter against the actual loaded candle-data range. That filter
      //! is grounded in reality (real firstDataTime/lastDataTime from the
      //! candle series) — we don't duplicate it here because fetchDateRange
      //! in this layer is anchored to the FIRST tick time (useLastTickTime
      //! gates at windowDays-1 = 6 days for 1m) and therefore *does not
      //! advance as the sim progresses*, causing valid current-sim markers
      //! with bar_start > first-tick-time to get wrongly rejected upstream.

      //! For exits the order side is the *closing* side (BUY closes SHORT, SELL closes LONG),
      //!  which is the opposite of the position direction we're exiting. autoFlipPosition in
      //!  buildTradeMarker keys off position direction (defaults: long-exit aboveBar, short-exit
      //!  belowBar), so feeding raw order.side here flips every exit marker the wrong way.
      //!  Backtest path is unaffected because buildMarkersFromTrades reads trade.side directly.
      const orderSide = String(order.side || '').toUpperCase();
      const isEntry = action === 'enter_long' || action === 'enter_short';
      const side = isEntry
        ? (['SELL', 'SHORT'].includes(orderSide) ? 'SHORT' : 'LONG')
        : (['SELL', 'SHORT'].includes(orderSide) ? 'LONG' : 'SHORT');
      const stage = order.stage ?? 1;

      const signalNode = findSignalNode(nodes, action, stage);
      let outcome;
      if (!isEntry) {
        const entry = pairedEntry(order);
        const entrySide = String(entry?.side || '').toUpperCase();
        outcome = pricesToOutcome({
          side: entrySide === 'BUY' ? 'LONG' : entrySide === 'SELL' ? 'SHORT' : null,
          entryPrice: entry?.filled_price,
          exitPrice: order.filled_price,
        });
      }
      const marker = buildTradeMarker(
        { time, price: Number(order.filled_price), action, side, stage, outcome, exitReason: order.exit_reason || (order.leg_type || order.action) },
        signalNode,
      );
      if (marker) out.push(marker);
    }
    out.sort((a, b) => (a.time ?? 0) - (b.time ?? 0));
    return out;
  }, [orders, effectiveNodes, deployment?.timeframe, priorTrades, openTrades]); // eslint-disable-line react-hooks/exhaustive-deps

  //--> Derive chart mode from deployment.
  const mode = deployment?.mode ?? 'rd';

  //--> resolvedInstrumentId is declared ABOVE (moved up so the open-trade band hook + the
  //*   tick-time subscription share one source of truth).

  //--> Subscribe to per-instrument tick time. Debounced at 60s so this only
  //*   triggers a re-fetch when the chart window needs to shift by ≥1 bar.
  const { lastTickTime, tickType } = useLastTickTime(resolvedInstrumentId);

  //--> Historic fetch window. Uses lastTickTime as anchor when available so
  //*   simulated/historic sessions stay centred on the replayed date, not today.
  const fetchDateRange = useFetchDateRange({ mode, timeframe: deployment?.timeframe, lastObservedTickTime: lastTickTime });

  //--> Fetch historical orders scoped to the current chart window so markers
  //*   that fall outside the visible bar range aren't loaded at all.
  //*   Re-runs when the window shifts (sim drifts past windowDays-1) so the
  //*   backlog stays in sync with what the chart is showing.
  const ordersWindow = useMemo(() => historyFetchWindow({
    mode,
    timeframe: deployment?.timeframe,
    fetchDateRange,
  }), [mode, deployment?.timeframe, fetchDateRange]);
  const fromBarStartIso = ordersWindow?.startDate?.toISOString() ?? null;
  const toBarStartIso = ordersWindow?.endDate?.toISOString() ?? null;

  useEffect(() => {
    if (!strategyInstanceId) return;
    //! Wait for a real window before fetching — pre-window fetch returns the
    //! whole lifetime and is exactly the bug this replaces.
    if (!fromBarStartIso || !toBarStartIso) return;
    let active = true;

    (async () => {
      try {
        const rows = await fetchOrdersWindow(fromBarStartIso, toBarStartIso);
        if (active) setHistoricalOrders(rows);
      } catch (err) {
        console.error('[LiveAnalysisTab] windowed historical orders fetch failed', err);
      }
    })();

    return () => { active = false; };
  }, [strategyInstanceId, fromBarStartIso, toBarStartIso, fetchOrdersWindow]);

  const handleGapRepair = useCallback(async ({ start, end }) => {
    const requestedStrategyInstanceId = strategyInstanceIdRef.current;
    try {
      const rows = await fetchOrdersWindow(start, end);
      if (strategyInstanceIdRef.current !== requestedStrategyInstanceId) return;
      setGapOrders((previous) => {
        const byId = new Map(previous.map((order) => [order.id || order.order_id, order]));
        rows.forEach((order) => byId.set(order.id || order.order_id, order));
        return Array.from(byId.values());
      });
    } catch (err) {
      console.error('[LiveAnalysisTab] gap orders fetch failed', err);
    }
    if (strategyInstanceIdRef.current !== requestedStrategyInstanceId) return;
    refetchOpenTrade();
  }, [fetchOrdersWindow, refetchOpenTrade]);

  //--> Live tick object for the resolved instrument — feeds SmartChart useLiveUpdates.
  //*   Uses the same deploymentTicksRef + rAF-coalesced versions.deploymentTicks
  //*   mechanism as useDeploymentLtp so no extra WS subscription is needed.
  const liveTick = useInstrumentTick(resolvedInstrumentId);

  //! Board #73 — engineDecision carries the engine's REAL post-gate verdict for
  //!  the most recently triggered signal node (verdict/action/stage/node_id/
  //!  reason/since_ms), destructured from the SAME hook call as liveNodeResults
  //!  so the two can never describe different bars. Passed to StrategyAnalysisView
  //!  as its own prop (never merged into liveNodeResults) — see :468-487 below.
  //! 10502-5 — the Provider's ONE ref-counted subscription per deployment (shared with
  //!  MonitoringTab); the legacy useStrategyExecution shim is deleted. Retained replay on
  //!  subscribe (10502-4) seeds the first value; the hook returns a STABLE empty object
  //!  before that (EMPTY_NODE_RESULTS), never a fresh `{}` per render.
  const { liveNodeResults, lastEvalTime, barTimeframe: liveBarTimeframe, engineDecision } =
    useLiveExecution(deployment?.deployment_id);

  //--> Right-side panel (§E/§G) — panel instrument via D36's chokepoint, never
  //*   config.instruments[0]. instrumentReady gates the Order body only (R1.1 refine).
  const { instrument: panelInstrument } = usePanelInstrument(deployment);
  const [panelTab, setPanelTab] = useState('depth');

  //--> D53 visual-gate fix-up — selection LIFTED here so the left RDWatchDashboard (via
  //*   StrategyAnalysisView's onItemClick/selectedNodeId passthrough) and the Indicators tab
  //*   list share ONE selectedNodeId (RDWatchDashboard.jsx:260-344 already supports this
  //*   contract; no second selection mechanism). A dashboard-card click both selects the node
  //*   AND switches the panel to the Indicators tab; a list-row click only selects (the panel
  //*   is already showing Indicators).
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  //* 50-14 — the ONE id-extraction + select helper (select only). The panel Dashboard copy calls it
  //*  directly (stays on its tab); the below-chart handler composes it with the Indicators jump.
  const selectDashboardItem = useCallback((node) => {
    const id = typeof node === 'string' ? node : node?.id;
    if (!id) return false;
    setSelectedNodeId(id);
    return true;
  }, []);
  const handleDashboardItemClick = useCallback((node) => {
    if (selectDashboardItem(node)) setPanelTab('indicators');
  }, [selectDashboardItem]);
  const handleIndicatorListSelect = useCallback((nodeId) => {
    setSelectedNodeId(nodeId);
  }, []);

  //--> Order freeze (D36/M2-3) — the PARENT freezes the first observed ltp>0 into its OWN
  //*   state exactly ONCE; reset to null when the resolved instrument changes so a roll
  //*   re-freezes fresh rather than carrying the old contract's price forward.
  const [frozenEntryPrice, setFrozenEntryPrice] = useState(null);
  useEffect(() => {
    if (frozenEntryPrice === null && liveTick?.ltp > 0) setFrozenEntryPrice(liveTick.ltp);
  }, [frozenEntryPrice, liveTick?.ltp]);
  //! Keyed on `resolvedInstrumentId` (the SYNCHRONOUS deployment-derived id), not
  //! `panelInstrument?.instrument_id` — the latter transitions undefined -> defined purely as
  //! an artefact of `usePanelInstrument`'s own async fetch resolving. A ref-tracked PREVIOUS
  //! value (rather than the bare dependency-array mount-always-fires behaviour) distinguishes a
  //! genuine ROLL (a new deployment.resolved_instrument_id) from the initial mount — a plain
  //! `useEffect(() => setFrozenEntryPrice(null), [resolvedInstrumentId])` fires on EVERY mount
  //! too, which would wipe a freeze already set by the effect above in the very same commit
  //! whenever a tick is already cached at mount time.
  const prevResolvedInstrumentIdRef = useRef(resolvedInstrumentId);
  useEffect(() => {
    if (prevResolvedInstrumentIdRef.current !== resolvedInstrumentId) {
      prevResolvedInstrumentIdRef.current = resolvedInstrumentId;
      setFrozenEntryPrice(null);
    }
  }, [resolvedInstrumentId]);

  /*>>> RESPONSIVE PANEL HOST (50-12 WP1) <<<*/
  //* Tier authority is useBreakpoint (hasSidebar = desktop|wide). >=1200 the panel sits in the 3/12
  //* grid cell; <1200 it lives in a bottom Sheet. Sheet unmounts its children on close, but the five
  //* panel bodies must keep their draft state — so the panel subtree is rendered ONCE through a portal
  //* into a stable `display: contents` host node that is MOVED between the grid slot and the Sheet slot.
  //* Closing the Sheet only detaches DOM; React state (order form draft, indicator draft) is never torn down.
  const { hasSidebar, isTablet } = useBreakpoint();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [panelHost] = useState(() => {
    const el = document.createElement('div');
    el.style.display = 'contents';
    return el;
  });
  const attachPanelHost = useCallback((node) => {
    if (node && panelHost.parentNode !== node) node.appendChild(panelHost);
  }, [panelHost]);
  useEffect(() => () => panelHost.remove(), [panelHost]);
  //--> Growing to desktop closes the drawer (the panel re-attaches to the grid cell).
  useEffect(() => {
    if (hasSidebar) setDrawerOpen(false);
  }, [hasSidebar]);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const sheetOpen = drawerOpen && !hasSidebar;
  const showPanelTrigger = !hasSidebar && configLoaded && Boolean(instrument);

  /*>>> COLLAPSIBLE RAIL + DASHBOARD HOST (50-14) <<<*/
  const [panelCollapsed, setPanelCollapsed] = useState(readPanelCollapsed);
  const panelCollapsedActive = hasSidebar && panelCollapsed;
  const setCollapsed = useCallback((next) => {
    setPanelCollapsed(next);
    writePanelCollapsed(next);
  }, []);
  const togglePanelCollapsed = useCallback(() => setCollapsed(!panelCollapsed), [setCollapsed, panelCollapsed]);
  const segmentedWrapRef = useRef(null);
  //--> A rail click unmounts the focused rail button, so hand focus to the Segmented's active option.
  //    (Segmented does not forward refs → query its wrapper.)
  const expandToTab = useCallback((tab) => {
    setPanelTab(tab);
    setCollapsed(false);
    requestAnimationFrame(() => {
      segmentedWrapRef.current?.querySelector('button[aria-pressed="true"]')?.focus();
    });
  }, [setCollapsed]);
  //--> Host node for the panel's second RDWatchDashboard (portalled by StrategyAnalysisView). The copy mounts
  //    only while visible: never into a collapsed rail, never into the detached host with the drawer closed.
  const [dashboardHostEl, setDashboardHostEl] = useState(null);
  const panelDashboardOn = panelTab === 'dashboard' && (hasSidebar ? !panelCollapsed : sheetOpen);
  //--> Segmented options (icon tabs, D21). Memoised: Segmented re-measures its thumb whenever `options` changes.
  //    Desktop/wide: icon + sr-only label; tablet drawer: icon + text; phone drawer: icons only.
  const segmentedOptions = useMemo(() => PANEL_TABS.map(({ value, label, icon: Icon }) => ({
    value,
    testId: `panel-tab-${value}`,
    icon: isTablet
      ? <Icon size={14} aria-hidden="true" />
      : <Tooltip content={label} placement="bottom"><span className="inline-flex"><Icon size={14} aria-hidden="true" /></span></Tooltip>,
    label: isTablet ? label : <span className="sr-only">{label}</span>,
  })), [isTablet]);

  //--> Right panel subtree (collapse row + Segmented + 5 always-mounted bodies) — rendered once, whichever slot hosts it.
  const panelContent = (
    <>
      <div
        data-testid="panel-header"
        className={`relative ${panelCollapsedActive ? 'flex flex-col items-center gap-1' : 'flex items-center gap-1'}`}
      >
        <div
          ref={segmentedWrapRef}
          aria-hidden={panelCollapsedActive ? 'true' : undefined}
          style={panelContentStyle(panelCollapsedActive)}
          className={`motion-reduce:!transition-none ${panelCollapsedActive ? 'absolute inset-x-0 top-0 opacity-0 invisible pointer-events-none' : 'flex-1 min-w-0 opacity-100 visible'}`}
        >
          <Segmented
            aria-label="Live Analysis panel"
            value={panelTab}
            onChange={setPanelTab}
            options={segmentedOptions}
          />
        </div>
        {hasSidebar && (
          <Tooltip content={panelCollapsed ? 'Expand panel' : 'Collapse panel'} placement="left">
            <button
              type="button"
              data-testid="panel-collapse-toggle"
              onClick={togglePanelCollapsed}
              aria-label={panelCollapsed ? 'Expand panel' : 'Collapse panel'}
              aria-expanded={!panelCollapsed}
              className={`flex items-center justify-center w-8 h-8 shrink-0 rounded-md text-fg-3 hover:bg-white dark:hover:bg-bg-3 hover:text-fg-2 transition-colors ${panelCollapsed ? 'order-first' : 'order-last'}`}
            >
              <FiChevronRight size={18} style={panelColumnStyle(panelCollapsed)} className={`transition-transform motion-reduce:!transition-none ${panelCollapsed ? 'rotate-180' : ''}`} />
            </button>
          </Tooltip>
        )}
        {panelCollapsedActive && PANEL_TABS.map(({ value, label, icon: Icon }) => (
          <Tooltip key={value} content={label} placement="left">
            <button
              type="button"
              data-testid={`rail-tab-${value}`}
              aria-label={label}
              aria-current={panelTab === value ? 'true' : undefined}
              onClick={() => expandToTab(value)}
              style={PANEL_RAIL_FADE_STYLE}
              className={`animate-fadeIn motion-reduce:!animate-none w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${panelTab === value ? 'text-[var(--color-accent)] bg-[rgba(var(--color-accent-rgb),0.14)]' : 'text-fg-3 hover:text-fg-2 hover:bg-bg-3'}`}
            >
              <Icon size={18} aria-hidden="true" />
            </button>
          </Tooltip>
        ))}
      </div>

      <div
        data-testid="panel-body"
        aria-hidden={panelCollapsedActive ? 'true' : undefined}
        style={panelContentStyle(panelCollapsedActive)}
        className={`bg-white dark:bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)] p-2 motion-reduce:!transition-none ${hasSidebar ? 'flex-1 h-0 overflow-y-auto custom-scrollbar' : ''} ${panelCollapsedActive ? 'opacity-0 invisible pointer-events-none' : 'opacity-100 visible'}`}
      >
        <div className={panelTab === 'depth' ? '' : 'hidden'} data-testid="panel-body-depth">
          <DepthPanelBody liveTick={liveTick} />
        </div>

        <div className={panelTab === 'order' ? '' : 'hidden'} data-testid="panel-body-order">
          {panelInstrument && frozenEntryPrice !== null
            ? <OrderPanelBody key={panelInstrument.instrument_id} instrument={panelInstrument} initialPrice={frozenEntryPrice} />
            : <WaitingForLivePricePlaceholder />}
        </div>

        <div className={panelTab === 'algo' ? '' : 'hidden'} data-testid="panel-body-algo">
          <AlgoPanelBody deploymentId={deployment?.deployment_id} />
        </div>

        <div className={panelTab === 'indicators' ? '' : 'hidden'} data-testid="panel-body-indicators">
          <IndicatorsPanelTab
            store={overridesStore}
            versionNodes={strategyConfig.nodes}
            edges={strategyConfig.edges}
            selectedNodeId={selectedNodeId}
            onSelectNode={handleIndicatorListSelect}
            categoryByIndicator={categoryByIndicator}
            allIndicatorMetadata={indicatorMetadata}
          />
        </div>

        {/* 50-14: the body div never unmounts; the dashboard copy inside the host mounts only while visible. */}
        <div className={panelTab === 'dashboard' ? '' : 'hidden'} data-testid="panel-body-dashboard">
          <div ref={setDashboardHostEl} data-testid="panel-dashboard-host" />
        </div>
      </div>
    </>
  );

  return (
    <div className="flex-1 w-full h-full flex flex-col min-h-0">
      {/*--> rev4 account-filter dropdown: rendered ONLY when >1 account group (dormant on today's
           single-account data — array-of-1 → no dropdown, pixel-identical to the single-band path).
           ALL (null) renders every group's labeled band; single-select renders one. */}
      {/*--> 50-12 (critic F2): on tablet/phone the drawer trigger shares this row — it is never a grid item,
           so the chart stays the grid's only row on compact tiers. */}
      {(openTrades.length > 1 || showPanelTrigger) && (
        <div className="flex items-center gap-2 px-1 pb-2">
          {openTrades.length > 1 && (<>
          <span className="text-xs text-fg-2">Account</span>
          <Select
            size="sm"
            value={selectedAccountId ?? ''}
            onChange={(e) => setSelectedAccountId(e.target.value || null)}
            aria-label="Filter chart bands by account"
          >
            <option value="">All accounts</option>
            {/* R1.2.4: key widened to (account_id, entry_stage) — two concurrent stage groups on the
                SAME account now share account_id, so a bare account_id key would collide (React
                duplicate-key). account_label (server-suffixed "· stage N" on collision) still displays
                as-is; the select's filter value stays account_id (account-level filter, unchanged). */}
            {openTrades.map((g) => (
              <option key={`${g.account_id ?? 'none'}-stage${g.entry_stage ?? 1}`} value={g.account_id ?? ''}>{g.account_label}</option>
            ))}
          </Select>
          </>)}
          {showPanelTrigger && (
            <Button
              className="ml-auto"
              variant="secondary"
              size="sm"
              aria-label="Live Analysis panel"
              aria-haspopup="dialog"
              aria-expanded={sheetOpen}
              onClick={() => setDrawerOpen(true)}
            >
              Panel
            </Button>
          )}
        </div>
      )}
      {configLoaded && instrument ? (
        /*>>> §E GRID — one template table (PANEL_GRID_TEMPLATE): desktop/wide chart + panel (or 40px rail); tablet/phone chart only + drawer <<<*/
        <div
          data-testid="live-analysis-grid"
          className="flex-1 min-h-0 grid gap-2 transition-[grid-template-columns] motion-reduce:!transition-none"
          style={{ ...panelColumnStyle(panelCollapsed), gridTemplateColumns: !hasSidebar ? PANEL_GRID_TEMPLATE.compact : panelCollapsed ? PANEL_GRID_TEMPLATE.collapsed : PANEL_GRID_TEMPLATE.expanded }}
        >
          <div data-testid="live-analysis-chart-cell" className="min-h-0 min-w-0">
            <StrategyAnalysisView
              categoryByIndicator={categoryByIndicator}
              nodes={effectiveNodes}
              //* 50-21 R10-T4 — node id -> record entry (saved ⊕ draft): condition names/notes for the dashboard
              visualOverrides={overridesStore.effectiveOverrides}
              computationNodes={strategyConfig.nodes}
              edges={strategyConfig.edges}
              instrument={instrument}
              instruments={Array.isArray(strategyConfig.instruments) && strategyConfig.instruments.length > 0
                ? strategyConfig.instruments
                : [instrument]}
              general={strategyConfig.general}
              liveTick={liveTick || undefined}
              user={user}
              levelHistory={levelHistory}
              positionLevels={positionLevels}
              levelDisplayNodes={levelDisplayNodes}
              rolloverDays={rolloverDays}
              isContinuous={isContinuous}
              strategyId={deployment?.strategy_id || 'live'}
              deploymentId={deployment?.deployment_id || null}
              mode={mode}
              fetchDateRange={fetchDateRange}
              liveNodeResults={liveNodeResults}
              lastEvalTime={lastEvalTime}
              liveBarTimeframe={liveBarTimeframe}
              engineDecision={engineDecision}
              tickType={tickType}
              tradeMarkers={tradeMarkers}
              resolvedInstrumentId={resolvedInstrumentId}
              onGapRepair={handleGapRepair}
              selectedNodeId={selectedNodeId}
              onItemClick={handleDashboardItemClick}
              panelDashboardHost={panelDashboardOn ? dashboardHostEl : null}
              onPanelItemClick={selectDashboardItem}
            />
          </div>

          {/* RIGHT: Depth / Order / Algo / Indicators / Dashboard — all 5 bodies ALWAYS mounted
              (hidden via the `hidden` class idiom, SmartTrade's pattern), so in-progress
              state (order form draft, indicator draft) survives a tab switch AND a drawer
              close/reopen (the subtree is one portal into the movable `panelHost`). */}
          {createPortal(panelContent, panelHost)}
          {hasSidebar && (
            <div ref={attachPanelHost} data-testid="live-analysis-panel-cell" className="min-w-0 flex flex-col gap-2 h-full overflow-hidden pb-1" />
          )}
        </div>
      ) : (
        <div className="flex h-full items-center justify-center rounded-xl border border-dashed border-[var(--divider)] bg-bg-2/40 text-sm text-fg-2">
          {configLoaded ? 'No instrument associated with this deployment.' : 'Loading strategy…'}
        </div>
      )}
      {/*--> tablet/phone drawer: its body is only the attach slot for the movable panel host (never a grid item) */}
      <Sheet open={sheetOpen} onClose={closeDrawer} title="Live Analysis panel">
        <div ref={attachPanelHost} className="flex flex-col gap-2 pb-2" />
      </Sheet>
    </div>
  );
};

export default LiveAnalysisTab;
